<template>
  <div class="crop-container">
    <div class="crop-canvas">
      <div ref="konvaRef"></div>
      <div class="crop-view">
        <div class="top"></div>
        <div class="middle" :style="displayDropViewStyle"></div>
        <div class="bottom"></div>
      </div>
    </div>
    <div class="buttons flex-center mt-[16px] gap-x-[10px] text-[14px]">
      <a-button-group class="op-btn-group">
        <a-radio-button class="btn-zoom-out" @click="onZoomOut">
          <iconpark-icon name="zoom-out"></iconpark-icon>
        </a-radio-button>
        <a-radio-button class="btn-zoom-in" @click="onZoomIn">
          <iconpark-icon name="zoom-in"></iconpark-icon>
        </a-radio-button>
      </a-button-group>
      <a-button class="op-btn btn-reset" @click="onReset">
        <iconpark-icon name="reset-circle"></iconpark-icon>
        {{ $t('hui-fu-mo-ren') }}
      </a-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Konva from 'konva';
const props = defineProps<{
  src: string;
  width: number;
  height: number;
  ratio: {
    h: number;
    w: number;
  };
}>();

const konvaRef = ref<HTMLDivElement>();

const konvaCtx = reactive<{
  stage?: Konva.Stage;
  layer?: Konva.Layer;
  image?: Konva.Image;
  zoom: number;
}>({
  zoom: 1,
});

const getImageData = async () => {
  if (!konvaCtx.stage || !konvaCtx.layer || !konvaCtx.image) {
    return null;
  }

  const imageData = await konvaCtx.stage.toDataURL({
    pixelRatio: props.ratio.w / props.width,
  });

  const img = await getImageResource(imageData);
  const cvs = document.createElement('canvas');
  cvs.width = props.ratio.w;
  cvs.height = props.ratio.h;
  const ctx = cvs.getContext('2d')!;
  const sx = 0;
  const sy = -(img.height - props.ratio.h) / 2;
  ctx.drawImage(img, sx, sy, img.width, img.height);
  return cvs.toDataURL('image/png', 1);
};

defineExpose({
  getImageData,
});

const displayDropViewStyle = computed(() => ({
  width: '100%',
  height: `${(props.ratio.h / props.ratio.w) * props.width}px`,
}));

const onZoomIn = () => {
  konvaCtx.zoom += 0.05;
};

const onZoomOut = () => {
  konvaCtx.zoom -= 0.05;
};

const onZoomChange = (zoom: number) => {
  if (!konvaCtx.stage || !konvaCtx.layer || !konvaCtx.image) {
    return;
  }
  if (zoom <= 0) {
    return;
  }
  const { stage } = konvaCtx;
  const oldScale = stage.scaleX();
  const center = {
    x: stage.width() / 2,
    y: stage.height() / 2,
  };
  const relatedTo = {
    x: (center.x - stage.x()) / oldScale,
    y: (center.y - stage.y()) / oldScale,
  };
  stage.scale({
    x: zoom,
    y: zoom,
  });
  stage.position({
    x: center.x - relatedTo.x * zoom,
    y: center.y - relatedTo.y * zoom,
  });
  stage.batchDraw();
};

watch(() => konvaCtx.zoom, onZoomChange);

const getImageResource = (link: string) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.src = link;
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    return img;
  });
};

const reset = async (link: string) => {
  if (!konvaCtx.stage || !konvaCtx.layer) {
    return;
  }
  konvaCtx.image?.remove();
  const img = await getImageResource(link);
  const image = new Konva.Image({
    image: img,
    x: props.width / 2 - img.width / 2,
    y: props.height / 2 - img.height / 2,
    draggable: true,
    zIndex: 1,
  });
  image.on('mouseenter', function () {
    konvaCtx.stage!.container().style.cursor = 'grab';
  });
  image.on('mouseleave', function () {
    konvaCtx.stage!.container().style.cursor = 'default';
  });
  konvaCtx.layer.add(image);
  konvaCtx.image = image;

  onZoomChange(+(props.width / img.width));
};

const onReset = () => {
  reset(props.src);
};

watch(() => props.src, reset);

onMounted(() => {
  if (!konvaRef.value) {
    return;
  }
  const stage = new Konva.Stage({
    container: konvaRef.value,
    width: props.width,
    height: props.height,
    scale: { x: konvaCtx.zoom, y: konvaCtx.zoom },
  });
  const layer = new Konva.Layer();
  stage.add(layer);
  konvaCtx.stage = stage;
  konvaCtx.layer = layer;

  stage.on('wheel', e => {
    e.evt.preventDefault();
    const oldScale = stage.scaleX();
    let direction = e.evt.deltaY > 0 ? 1 : -1;
    if (e.evt.ctrlKey) {
      direction = -direction;
    }
    const newScale = direction > 0 ? oldScale + 0.05 : oldScale - 0.05;
    konvaCtx.zoom = newScale;
  });

  reset(props.src);
});
</script>

<style lang="scss" scoped>
.op-btn-group {
  padding: 0 8px;
  border-radius: 18px;
  border: 1px solid var(--back-4);
}

.btn-zoom-out,
.btn-zoom-in {
  border: none;
  padding-top: 3px;
}

.btn-zoom-in::before {
  background-color: var(--back-4);
}

.btn-reset {
  display: flex;
  align-items: center;
  border-radius: 18px;
  border: 1px solid var(--back-4);
  height: 36px;
  color: var(--text-1-70);
}

.op-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.crop-container {
  position: relative;
  width: 100%;
}

.crop-view {
  pointer-events: none;
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  flex-direction: column;
  .top,
  .bottom {
    flex: 1;
    background-color: rgba(0, 0, 0, 0.6);
  }
  .middle {
    border: 1px dashed var(--back-6);
  }
}

.crop-canvas {
  position: relative;
  display: block;
  width: 100%;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.2);
}
</style>
